import { create } from "zustand";
import { persist } from "zustand/middleware";
import { AUTH_TOKEN_KEY, USER_DATA_KEY } from "@/utils/auth";
import {
  ACTIVITY_WRITE_MS,
  DEFAULT_LOCK_TIMEOUT_MS,
  MAX_PATTERN_ATTEMPTS,
  appLockUserKey,
  createSalt,
  hashPattern,
  nextFailure,
  shouldLock,
} from "@/utils/appLock";

const STORAGE_KEY = "app-lock-v1";

function readJson(key) {
  try {
    if (typeof localStorage === "undefined") return null;
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function loadRecords() {
  const persisted = readJson(STORAGE_KEY);
  const records = persisted?.state?.records;
  return records && typeof records === "object" ? records : {};
}

function loadSessionUserKey() {
  const token = typeof localStorage === "undefined" ? null : localStorage.getItem(AUTH_TOKEN_KEY);
  if (!token) return null;
  return appLockUserKey(readJson(USER_DATA_KEY));
}

function initialState() {
  const records = loadRecords();
  const activeUserKey = loadSessionUserKey();
  const record = activeUserKey ? records[activeUserKey] : null;
  return {
    records,
    activeUserKey,
    locked: !!(record && shouldLock(record)),
    failedAttempts: 0,
    lockoutUntil: 0,
  };
}

function writeActivity(set, get, { force = false } = {}) {
  const { locked, activeUserKey, records } = get();
  if (locked || !activeUserKey) return;
  const record = records[activeUserKey];
  if (!record?.enabled) return;
  const now = Date.now();
  if (!force && now - (record.lastActivityAt || 0) < ACTIVITY_WRITE_MS) return;
  set({
    records: {
      ...records,
      [activeUserKey]: { ...record, lastActivityAt: now },
    },
  });
}

export const useAppLockStore = create(
  persist(
    (set, get) => ({
      ...initialState(),

      bindRestoredSession(userKey) {
        if (!userKey) return;
        const record = get().records[userKey];
        set({
          activeUserKey: userKey,
          locked: !!(record && shouldLock(record)),
          failedAttempts: 0,
          lockoutUntil: 0,
        });
      },

      bindFreshSession(userKey) {
        if (!userKey) return;
        const { records } = get();
        const record = records[userKey];
        const nextRecords = record?.enabled
          ? { ...records, [userKey]: { ...record, lastActivityAt: Date.now() } }
          : records;
        set({
          records: nextRecords,
          activeUserKey: userKey,
          locked: false,
          failedAttempts: 0,
          lockoutUntil: 0,
        });
      },

      releaseLockSession() {
        set({ activeUserKey: null, locked: false, failedAttempts: 0, lockoutUntil: 0 });
      },

      touch() {
        writeActivity(set, get);
      },

      markHidden() {
        writeActivity(set, get, { force: true });
      },

      lockIfIdle() {
        const { locked, activeUserKey, records } = get();
        if (locked || !activeUserKey) return;
        const record = records[activeUserKey];
        if (record && shouldLock(record)) set({ locked: true });
      },

      async checkPattern(pattern) {
        const started = get();
        const record = started.activeUserKey ? started.records[started.activeUserKey] : null;
        if (!record?.enabled || !record.patternHash || !record.salt) {
          return { ok: false, reason: "missing" };
        }
        if (started.lockoutUntil > Date.now()) {
          return { ok: false, reason: "lockout", lockoutUntil: started.lockoutUntil, remaining: 0 };
        }

        const hash = await hashPattern(pattern, record.salt);
        const current = get();
        if (current.activeUserKey !== started.activeUserKey) {
          return { ok: false, reason: "missing" };
        }
        if (hash !== record.patternHash) {
          const failure = nextFailure(current.failedAttempts);
          set({
            failedAttempts: failure.failedAttempts,
            lockoutUntil: failure.lockoutUntil,
            locked: failure.lockedOut || current.locked,
          });
          return {
            ok: false,
            reason: failure.lockedOut ? "lockout" : "mismatch",
            remaining: failure.lockedOut ? 0 : MAX_PATTERN_ATTEMPTS - failure.failedAttempts,
            lockoutUntil: failure.lockoutUntil,
          };
        }

        set({ failedAttempts: 0, lockoutUntil: 0 });
        return { ok: true };
      },

      async unlock(pattern) {
        const result = await get().checkPattern(pattern);
        if (!result.ok) return result;
        const { activeUserKey, records } = get();
        const record = records[activeUserKey];
        if (record) {
          set({
            locked: false,
            records: {
              ...records,
              [activeUserKey]: { ...record, lastActivityAt: Date.now() },
            },
          });
        } else {
          set({ locked: false });
        }
        return { ok: true };
      },

      async savePattern(pattern) {
        const userKey = get().activeUserKey;
        if (!userKey) return false;
        const salt = createSalt();
        const patternHash = await hashPattern(pattern, salt);
        const previous = get().records[userKey];
        set({
          locked: false,
          failedAttempts: 0,
          lockoutUntil: 0,
          records: {
            ...get().records,
            [userKey]: {
              enabled: true,
              patternHash,
              salt,
              timeoutMs: previous?.timeoutMs || DEFAULT_LOCK_TIMEOUT_MS,
              lastActivityAt: Date.now(),
            },
          },
        });
        return true;
      },

      setTimeoutMs(timeoutMs) {
        const { activeUserKey, records } = get();
        const record = activeUserKey ? records[activeUserKey] : null;
        if (!record?.enabled) return;
        set({
          records: {
            ...records,
            [activeUserKey]: { ...record, timeoutMs },
          },
        });
      },

      forgetActivePattern({ keepLocked = false } = {}) {
        const { activeUserKey, records, locked } = get();
        const nextRecords = { ...records };
        if (activeUserKey) delete nextRecords[activeUserKey];
        set({
          records: nextRecords,
          locked: keepLocked ? locked : false,
          failedAttempts: 0,
          lockoutUntil: 0,
        });
      },
    }),
    {
      name: STORAGE_KEY,
      partialize: (state) => ({ records: state.records }),
    }
  )
);
